import React, { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Col, Row } from 'react-bootstrap'
import { Button } from '../../components/Button/Button'
import { Scorecard as ScorecardModel, ScorecardResult } from '../../models/Scorecard'
import { ClientProfileBlock } from './ClientProfileBlock'
import { formatDate, ScorecardLabelLevel } from '../../common/clientProfile'
import { parseMetadata } from '../../apps/professional-app/fa/MeetingSummary/utils'
import classNames from 'classnames'
import { removeEmptyItems } from '../../common/util'

interface ClientProfileFormBodyProps {
  onSubmit: (data: { [key: string]: any }) => Promise<void>
  onReset: () => void
  scorecard: ScorecardModel
  scorecardResult: ScorecardResult
}

export const ClientProfileFormBody: React.FC<ClientProfileFormBodyProps> = ({ onSubmit, onReset, scorecard, scorecardResult }) => {
  const { formState, handleSubmit, control } = useFormContext()
  const { isValid, isDirty } = formState
  const formValues = useWatch({ control: control })

  const filteredFields = useMemo(() => {
    if (!formValues) {
      return []
    }
    return Object.entries(removeEmptyItems(formValues))
  }, [formValues])

  const shouldRenderBlockLabel = (() => {
    if (!scorecard.metadata) {
      return true
    }
    const metadata = parseMetadata(scorecard.metadata)
    return !(metadata?.labelConfig?.[ScorecardLabelLevel.BLOCK] === 'skip')
  })()

  if (!formValues) {
    return null
  }

  return (
    <div>
      <Row className='zocks-gap-1.25'>
        <Col className='d-flex justify-content-between align-items-center'>
          <div>
            <span className='scorecard-name'>{scorecard.name}</span>
          </div>
          {scorecardResult && (
            <Row className='d-flex flex-column justify-content-end'>
              <Col className='d-flex mb-0 fw-bold justify-content-end text-uppercase scorecard-last-changed'>Last Changed</Col>
              <Col className='scorecard-last-changed-value'>{formatDate(new Date(scorecardResult.createdAt))}</Col>
            </Row>
          )}
        </Col>
        <div>
          <span>{scorecard.description}</span>
        </div>
      </Row>
      <Row className='mt-3'>
        <Row
          className={classNames('d-flex', {
            'zocks-gap-2.5': shouldRenderBlockLabel,
            'gap-1': !shouldRenderBlockLabel,
          })}
        >
          {filteredFields.map(([key, value]: [string, any]) => {
            return (
              <ClientProfileBlock
                key={`body.${key}`}
                vkey={key}
                value={value}
                scorecard={scorecard}
                scorecardResult={scorecardResult!}
                schema={scorecard.descriptor['properties'][key]}
              />
            )
          })}

          <Row>
            <Col className='d-flex gap-2'>
              <Button variant='primary' onClick={handleSubmit(onSubmit)} disabled={!isDirty || !isValid}>
                Save
              </Button>
              <Button variant='secondary' onClick={onReset} disabled={!isDirty}>
                Reset
              </Button>
            </Col>
          </Row>
        </Row>
      </Row>
    </div>
  )
}
